import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Popconfirm, Segmented, Space, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { FilterItem, FilterItemLabel, TableWrapper, TabsWrapper, TenantRolesManagementWrapper, TenantUsersWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper, TypeWrapper } from "./TenantUser.styled";
import StringUtils from "../../../../../../util/StringUtils";
import Table, { ColumnType } from "antd/es/table";
import moment from "moment";
import TenantUserApi from "../../../../../../api/TenantUserApi";
import CreateTenantUserWizard from "./create-tenant-user-wizard/CreateTenantUserWizard";
import CreateTenantRoleWizard from "./create-tenant-role-wizard/CreateTenantRoleWizard";


interface TenantUsersProps {
    tenantView: any,
}

const TenantUsers: React.FC<TenantUsersProps> = (props)=> {
    let [type, setType] = useState('USERS');

    let onTypeChange = (type)=> {
        setType(type);
    };

    return <TenantUsersWrapper>
        <TabsWrapper>
            {
                type === 'USERS' && <TenantUsersManagement {...props} />
            }
            {
                type === 'ROLES' && <TenantRolesManagement {...props} />
            }
        </TabsWrapper>

        <TypeWrapper>
            <Segmented 
                options={[
                    {label: '用户', value: 'USERS'},
                    {label: '角色', value: 'ROLES'},
                ]} 
            onChange={onTypeChange} />
        </TypeWrapper>
    </TenantUsersWrapper>
};

const TenantUsersManagement: React.FC<TenantUsersProps> = (props) => {
    let tenantView = props.tenantView;
    let tenantId = tenantView.tenant.id;

    // api
    let tenantUserApi = new TenantUserApi();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [userViews, setUserViews] = useState([]);
    let [filterUsers, setFilterUsers] = useState([]);
    let [editingUser, setEditingUser] = useState(null);
    let [openUserModal, setOpenUserModal] = useState(false);

    let [loading, setLoading] = useState(true);
    let [search, setSearch] = useState("");
    let [tableParams, setTableParams] = useState<any>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    // events
    let onTypeChange = (value: string) => {
        props.tenantView.setTenantType(value)
    };

    let viewUserDetails = (userView: any)=> {
        setEditingUser(userView);
        setOpenUserModal(true);
    };

    let onSearchChange = (e)=> {
        let search = e.target.value;
        setSearch(search);
        
        let filterUsers = [].concat(userViews);
        if (!StringUtils.isEmpty(search)) {
            filterUsers = userViews.filter((user)=> {
                if (user.name.indexOf(search) >= 0) {
                    return true;
                }
                if (user.email.indexOf(search) >= 0) {
                    return true;
                }
                if (user.phoneNumber.indexOf(search) >= 0) {
                    return true;
                }
                return false;
            });
        }
        setFilterUsers(filterUsers);
    }

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadUsers();
    };

    let onCreateUserClick = ()=> {
        setEditingUser(null);
        setOpenUserModal(true);
    };

    let onDeleteUser = async (userView: any)=> {
        await tenantUserApi.deleteTenantUser(tenantId, userView.user.id);
        messageApi.open({
            type: 'success',
            content: '删除用户成功',
          });
        loadUsers();
    }

    // load device views
    let loadUsers = async ()=> {
        // loading animation
        setLoading(true);
        let users = (await tenantUserApi.getTenantUserViews(tenantId)).data.data;
        setUserViews(users);
        setFilterUsers([].concat(users));
        setLoading(false);
    };

    let getUserStatusText = (user)=> {
        return user.disabled ? '禁用' : '正常';
    };

    // effect
    useEffect(()=> {
        loadUsers();
    }, []);

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '用户名',
            dataIndex: 'user.name',
            key: 'user.name',
            align: 'center',
            render: (text, record, index)=> {
                return <a onClick={()=> viewUserDetails(record)}>{record.user.name}</a>
            }
        },
        {
            title: '电子邮箱',
            dataIndex: 'user.email',
            key: 'user.email',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.user.email)}</span>
            }
        },
        {
            title: '电话号码',
            dataIndex: 'user.phoneNumber',
            key: 'user.phoneNumber',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.user.phoneNumber)}</span>
            }
        },
        {
            title: '最后登录时间',
            dataIndex: 'user.lastLoginTime',
            key: 'user.lastLoginTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ record.user.lastLoginTime ? moment(parseInt(record.user.lastLoginTime)).format("YYYY-MM-DD HH:mm:ss") : 'N/A' }</span>
            }
        },
        {
            title: '角色',
            dataIndex: 'roles',
            key: 'roles',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ record.roles && record.roles.length > 0 ? record.roles.map(e=> e.name).join(',') : 'N/A' }</span>
            }
        },
        {
            title: '状态',
            dataIndex: 'user.disabled',
            key: "user.disabled",
            align: 'center',
            render: (text, record, index)=> {
                return getUserStatusText(record);
            }
        },
        {
            title: '备注',
            dataIndex: 'user.remarks',
            key: 'user.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.user.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> viewUserDetails(record)}>查看详情</a>
                    { record.name != 'admin' && <Popconfirm
                            title="删除用户"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认要删除此用户么?</div> }
                            onConfirm={()=> onDeleteUser(record)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>删除用户</a>
                    </Popconfirm> }
                </Space>
            }
        }
    ];

    // render
    return <TenantUsersWrapper>
        {contextHolder}
        <ToolBarWrapper>
            <ToolBarLeftWrapper>
                <Space size="middle">
                    <Button type="primary" icon={<PlusOutlined />} onClick={onCreateUserClick}>添加用户</Button>
                </Space>
            </ToolBarLeftWrapper>

            <ToolBarRightWrapper>
                <FilterItem>
                    <Input 
                        placeholder="请输入关键字" 
                        suffix={<SearchOutlined />} 
                        size="middle" 
                        value={search} 
                        onChange={(e)=> onSearchChange} 
                    />
                </FilterItem>
            </ToolBarRightWrapper>

            <Modal open={openUserModal} 
                onClose={()=> setOpenUserModal(false)} 
                destroyOnClose={true} 
                footer={null} 
                closable={false}
                width={1000}
                >
                <CreateTenantUserWizard onClose={()=> setOpenUserModal(false)} onComplete={()=> loadUsers()} tenantId={tenantId} editingUserId={editingUser?.user.id} />
            </Modal>
        </ToolBarWrapper>
        <TableWrapper>
            <Table
                rowKey={(record) => record.user.id}
                columns={columns}
                dataSource={userViews}
                pagination={tableParams.pagination}
                loading={loading}
            />
        </TableWrapper>
    </TenantUsersWrapper>
};

const TenantRolesManagement: React.FC<TenantUsersProps> = (props) => {
    let tenantView = props.tenantView;
    let tenantId = tenantView.tenant.id;

    // api
    let tenantUserApi = new TenantUserApi();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [roles, setRoles] = useState([]);
    let [filterRoles, setFilterRoles] = useState([]);
    let [editingRole, setEditingRole] = useState(null);
    let [openRoleModal, setOpenRoleModal] = useState(false);

    let [loading, setLoading] = useState(true);
    let [search, setSearch] = useState("");
    let [tableParams, setTableParams] = useState<any>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    // events

    let viewRoleDetails = (role: any)=> {
        setEditingRole(role);
        setOpenRoleModal(true);
    };

    let onSearchChange = (e)=> {
        let search = e.target.value;
        setSearch(search);
        
        let filterRoles = [].concat(roles);
        if (!StringUtils.isEmpty(search)) {
            filterRoles = roles.filter((role)=> {
                if (role.name.indexOf(search) >= 0) {
                    return true;
                }
                if (role.email.indexOf(search) >= 0) {
                    return true;
                }
                return false;
            });
        }
        setFilterRoles(filterRoles);
    }

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadRoles();
    };

    let onCreateRoleClick = ()=> {
        setEditingRole(null);
        setOpenRoleModal(true);
    };

    let onDeleteRole = async (role: any)=> {
        await tenantUserApi.deleteTenantRole(tenantId, role.id);
        messageApi.open({
            type: 'success',
            content: '删除角色成功',
          });
        loadRoles();
    }

    // load device views
    let loadRoles = async ()=> {
        // loading animation
        setLoading(true);
        let roles = (await tenantUserApi.getTenantRoles(tenantId)).data.data;
        setRoles(roles);
        setFilterRoles([].concat(roles));
        setLoading(false);
    };

    // effect
    useEffect(()=> {
        loadRoles();
    }, []);

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '名称',
            dataIndex: 'role.name',
            key: 'role.name',
            align: 'center',
            render: (text, record, index)=> {
                return <a onClick={()=> viewRoleDetails(record)}>{record.name}</a>
            }
        },
        {
            title: '备注',
            dataIndex: 'user.remarks',
            key: 'user.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> viewRoleDetails(record)}>查看详情</a>
                    { record.name != 'admin' && <Popconfirm
                            title="删除角色"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认要删除此角色么?</div> }
                            onConfirm={()=> onDeleteRole(record)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>删除角色</a>
                    </Popconfirm> }
                </Space>
            }
        }
    ];

    // render
    return <TenantRolesManagementWrapper>
        {contextHolder}
        <ToolBarWrapper>
            <ToolBarLeftWrapper>
                <Space size="middle">
                    <Button type="primary" icon={<PlusOutlined />} onClick={onCreateRoleClick}>添加角色</Button>
                </Space>
            </ToolBarLeftWrapper>

            <ToolBarRightWrapper>
                <FilterItem>
                    <Input 
                        placeholder="请输入关键字" 
                        suffix={<SearchOutlined />} 
                        size="middle" 
                        value={search} 
                        onChange={(e)=> onSearchChange} 
                    />
                </FilterItem>
            </ToolBarRightWrapper>

            <Modal open={openRoleModal} 
                onClose={()=> setOpenRoleModal(false)} 
                destroyOnClose={true} 
                footer={null} 
                closable={false}
                width={1000}
                height={600}
                >
                <CreateTenantRoleWizard onClose={()=> setOpenRoleModal(false)} onComplete={()=> loadRoles()} tenantId={tenantView.tenant.id} editingRoleId={editingRole?.id} />
            </Modal>
        </ToolBarWrapper>
        <TableWrapper>
            <Table
                rowKey={(record) => record.id}
                columns={columns}
                dataSource={roles}
                pagination={tableParams.pagination}
                loading={loading}
            />
        </TableWrapper>
    </TenantRolesManagementWrapper>
};

export default TenantUsers;